/*
 * pw-shop-cta: resolves any "Shop now" link to the correct destination for
 * the active regional store.
 *
 * How merchants mark a CTA: put [data-pw-shop-cta] on an <a> or <button>.
 * Header SHOP NOW, PDP hero Shop-now, and any marketing-page CTA all qualify.
 *
 * Resolution order:
 *   1. Inline <script id="pw-store-config"> (theme-editor blocks)
 *   2. /assets/paragraph-stores.json (fallback for stores that haven't
 *      adopted the theme-editor section yet)
 *
 * For cta.type === 'partner' we show an interstitial modal before leaving.
 *
 * A button on a wine's page also carries data-pw-wine="red" or "white". In a
 * partner region it goes to that wine's listing (cta.wineUrls). The Tempranillo
 * falls back to cta.url; a Chardonnay without a listing gets the Notify me
 * version of the modal, which points at the footer email signup.
 */
(function () {
  'use strict';

  var MODAL_ID = 'pw-shop-cta-modal';
  var cache = null;

  function getCurrentStoreCode() {
    // settings.current_store_code is mirrored onto <html data-current-store>
    // by a tiny inline script in theme.liquid; we also check a meta tag for
    // resilience.
    var html = document.documentElement;
    var code = html.getAttribute('data-current-store');
    if (code) return code;
    var meta = document.querySelector('meta[name="pw-current-store"]');
    return meta ? meta.getAttribute('content') : null;
  }

  function getCurrentCountryCode() {
    var html = document.documentElement;
    var country = html.getAttribute('data-current-country');
    if (country) return country.toUpperCase();
    var meta = document.querySelector('meta[name="pw-current-country"]');
    return meta ? meta.getAttribute('content').toUpperCase() : null;
  }

  function loadConfig() {
    if (cache) return Promise.resolve(cache);
    var inline = document.getElementById('pw-store-config');
    if (inline) {
      try {
        cache = JSON.parse(inline.textContent);
        return Promise.resolve(cache);
      } catch (e) { /* fall through */ }
    }
    var link = document.querySelector('link[rel="preload"][as="fetch"][href*="paragraph-stores.json"]');
    var url = link ? link.href : '/cdn/shop/t/1/assets/paragraph-stores.json';
    // Prefer the data-stores-url from the selector component if present.
    var sel = document.querySelector('paragraph-store-selector[data-stores-url]');
    if (sel) url = sel.getAttribute('data-stores-url');
    return fetch(url, { cache: 'force-cache' })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(); })
      .then(function (data) { cache = data; return data; });
  }

  // Apply a market-specific override to the store config. Markets are keyed
  // by ISO-3166 country code in `store.marketOverrides`. Used to route Shop
  // Now to a partner retailer (e.g. globalwine.ch on the EU store's CH market)
  // without changing the store entry itself.
  function applyMarketOverride(store, country) {
    if (!store || !country) return store;
    var overrides = store.marketOverrides && store.marketOverrides[country];
    if (!overrides) return store;
    var merged = {};
    for (var k in store) merged[k] = store[k];
    for (var k2 in overrides) merged[k2] = overrides[k2];
    return merged;
  }

  function resolveDestination(store, wine) {
    if (!store) return null;
    var cta = store.cta || {};
    if (cta.type === 'partner' && wine) {
      var wineUrl = cta.wineUrls && cta.wineUrls[wine];
      if (wineUrl) return { url: wineUrl, type: 'partner', partnerName: cta.partnerName || store.name };
      if (wine === 'white') return { url: '#email-signup', type: 'notify', partnerName: cta.partnerName || store.name };
    }
    if (cta.type === 'partner' || cta.type === 'shopify') {
      return { url: cta.url, type: cta.type, partnerName: cta.partnerName || store.name };
    }
    // Default = self (the visitor's CURRENT Shopify store). The PDP
    // lives on the same domain, so we navigate relatively — never
    // prepend store.url, which is intended for cross-store navigation
    // and may legitimately be a placeholder (e.g. paragraph-wines.com
    // before that domain is set up). Building a full URL from a stale
    // store.url here previously sent every Shop Now click on the US
    // store to a non-existent paragraph-wines.com.
    //
    // Preserve the current Shopify Markets locale prefix (e.g. /en-ch
    // on the CH market URL) so the PDP resolves on the correct market.
    var currentPath = window.location.pathname || '/';
    var localeMatch = currentPath.match(/^\/[a-z]{2}(-[a-z]{2})?(?=\/|$)/i);
    var localePrefix = localeMatch ? localeMatch[0] : '';
    return { url: localePrefix + '/products/tempranillo', type: 'self', partnerName: null };
  }

  function ensureModal() {
    var existing = document.getElementById(MODAL_ID);
    if (existing) return existing;
    var modal = document.createElement('div');
    modal.id = MODAL_ID;
    modal.className = 'pw-shop-cta-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', MODAL_ID + '-title');
    modal.hidden = true;
    modal.innerHTML = ''
      + '<div class="pw-shop-cta-modal__backdrop" data-action="dismiss"></div>'
      + '<div class="pw-shop-cta-modal__panel" role="document">'
      +   '<h2 id="' + MODAL_ID + '-title" class="pw-shop-cta-modal__title">You\u2019re leaving PARAGRAPH</h2>'
      +   '<p class="pw-shop-cta-modal__msg">We\u2019re connecting you with <strong data-partner-name></strong> to complete your purchase. You\u2019ll continue on their website.</p>'
      +   '<div class="pw-shop-cta-modal__actions">'
      +     '<button type="button" class="pw-shop-cta-modal__cancel button" data-action="dismiss">Cancel</button>'
      +     '<a class="pw-shop-cta-modal__continue button button--primary" data-continue rel="noopener noreferrer">Continue</a>'
      +   '</div>'
      + '</div>';
    document.body.appendChild(modal);
    modal.addEventListener('click', function (e) {
      if (e.target.getAttribute('data-action') === 'dismiss') hideModal();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hidden) hideModal();
    });
    return modal;
  }

  // Partner handoff tracking is delegated to PW.track (pw-track.js
  // loaded earlier in <head>). The shared helper buffers events when
  // GA4/GTM isn't loaded yet and replays them once the analytics
  // globals appear, so we get reliable coverage even before the
  // analytics tags are wired up.
  function trackPartnerRedirect(dest, country) {
    if (window.PW && window.PW.track && window.PW.track.partnerRedirect) {
      window.PW.track.partnerRedirect({
        partner: dest.partnerName,
        url: dest.url,
        country: country
      });
    }
  }

  function showModal(dest, country) {
    var modal = ensureModal();
    var notify = dest.type === 'notify';
    modal.querySelector('.pw-shop-cta-modal__title').textContent = notify ? 'Coming soon' : 'You\u2019re leaving PARAGRAPH';
    var msg = modal.querySelector('.pw-shop-cta-modal__msg');
    if (notify) {
      msg.textContent = 'PARAGRAPH Chardonnay isn\u2019t available from ' + (dest.partnerName || 'our partner') + ' yet. Leave your email and we\u2019ll let you know when it is.';
    } else {
      msg.innerHTML = 'We\u2019re connecting you with <strong data-partner-name></strong> to complete your purchase. You\u2019ll continue on their website.';
      msg.querySelector('[data-partner-name]').textContent = dest.partnerName || 'our partner';
    }
    var link = modal.querySelector('[data-continue]');
    link.setAttribute('href', dest.url);
    link.textContent = notify ? 'Notify me' : 'Continue';
    if (notify) link.removeAttribute('target'); else link.setAttribute('target', '_blank');
    // Wire the redirect tracker once per click (cleared by replaceWith
    // pattern). Use mousedown so the event fires before the new tab
    // opens — Safari sometimes throttles synchronous JS during the
    // click → navigation transition.
    var clone = link.cloneNode(true);
    link.parentNode.replaceChild(clone, link);
    clone.addEventListener('click', function (ev) {
      if (!notify) { trackPartnerRedirect(dest, country); return; }
      /* Notify me: close the modal, then bring the footer signup into view with its email field focused. */
      ev.preventDefault();
      hideModal();
      var box = document.getElementById('email-signup');
      if (box) {
        box.scrollIntoView({ behavior: 'smooth', block: 'center' });
        var input = box.querySelector('input[type="email"]');
        if (input) setTimeout(function () { input.focus({ preventScroll: true }); }, 400);
      }
    });
    modal.hidden = false;
    requestAnimationFrame(function () { modal.setAttribute('data-visible', 'true'); });
    setTimeout(function () { clone.focus(); }, 50);
  }

  function hideModal() {
    var modal = document.getElementById(MODAL_ID);
    if (!modal) return;
    modal.removeAttribute('data-visible');
    setTimeout(function () { modal.hidden = true; }, 220);
  }

  function handleClick(e) {
    var el = e.target.closest('[data-pw-shop-cta]');
    if (!el) return;
    e.preventDefault();
    var code = getCurrentStoreCode();
    var country = getCurrentCountryCode();
    loadConfig().then(function (cfg) {
      if (!cfg || !Array.isArray(cfg.stores)) return;
      var store = null;
      for (var i = 0; i < cfg.stores.length; i++) {
        if (cfg.stores[i].code === code) { store = cfg.stores[i]; break; }
      }
      if (!store) store = cfg.stores[0];
      // Layer Markets override on top of the store config — e.g. EU store
      // viewed from CH market routes to the GlobalWine partner.
      store = applyMarketOverride(store, country);
      var dest = resolveDestination(store, el.getAttribute('data-pw-wine'));
      if (!dest || !dest.url) return;
      if (dest.type === 'partner' || dest.type === 'notify') {
        showModal(dest, country);
      } else {
        window.location.href = dest.url;
      }
    });
  }

  document.addEventListener('click', handleClick);
})();
